import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/element/button";
import Col from "../../../components/element/col";
import Icon from "../../../components/element/icon";
import Row from "../../../components/element/row";
import formatNumber from "../../../helpers/format.number";
import { setFilterData } from "../../../redux/reducer/product.reducer";
import useAppliedReferral from "../../../components/hook/use.applied.referral";
import useWindowDimension from "../../../components/hook/use.window.dimension";

const getOptionsById = (list = [], ids = []) =>
  (Array.isArray(list) ? list : Object.values(list || {}).flat())
    .filter((item) => ids.includes(item.id))
    .map((item) => ({
      id: item.id,
      value: item.label || item.name,
    }))
    .filter((item) => item.value);

export default function HomeFilteredValues({
  sticky = false,
  showCount = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isDesktop } = useWindowDimension();
  const { code, name, cancel } = useAppliedReferral();
  const [hasReachedShowing, setHasReachedShowing] = useState(false);
  // The header is sticky at the top, so the filter bar has to stop underneath
  // it rather than at the top of the viewport.
  const [stickyTop, setStickyTop] = useState(0);
  const { filterData, productList } = useSelector((state) => state.product);
  const { brandList, modelOption } = useSelector((state) => state.common);
  const category = filterData.category || "all";
  const categoryData = filterData[category] || {};
  const currentYear = new Date().getFullYear();
  const defaultYearRange = [2016, currentYear];
  const defaultPriceRange = [100000, 1000000];

  const isRangeDifferent = (range = [], defaults = []) =>
    Array.isArray(range) &&
    range.length === 2 &&
    (Number(range[0]) !== defaults[0] || Number(range[1]) !== defaults[1]);

  const summary = [
    ...getOptionsById(brandList, categoryData.brand).map((item) => ({
      label: "Brand",
      value: item.value,
      field: "brand",
      rawValue: item.id,
    })),
    ...getOptionsById(modelOption, categoryData.car_model).map((item) => ({
      label: "Model",
      value: item.value,
      field: "car_model",
      rawValue: item.id,
    })),
    ...(categoryData.body_type || []).map((value) => ({
      label: "Body",
      value,
      field: "body_type",
      rawValue: value,
    })),
    ...(categoryData.location || []).map((value) => ({
      label: "Location",
      value,
      field: "location",
      rawValue: value,
    })),
    ...(categoryData.show_room || []).map((value) => ({
      label: "Showroom",
      value,
      field: "show_room",
      rawValue: value,
    })),
    ...(isRangeDifferent(categoryData.year, defaultYearRange)
      ? [
          {
            label: "Year",
            value: `${categoryData.year[0]} - ${categoryData.year[1]}`,
            field: "year",
            resetValue: defaultYearRange,
          },
        ]
      : []),
    ...(isRangeDifferent(categoryData.price, defaultPriceRange)
      ? [
          {
            label: "Price",
            value: `RM ${formatNumber(categoryData.price[0])} - RM ${formatNumber(
              categoryData.price[1],
            )}`,
            field: "price",
            resetValue: defaultPriceRange,
          },
        ]
      : []),
  ];

  const removeFilter = (item) => {
    const nextValue = item.resetValue
      ? item.resetValue
      : (categoryData[item.field] || []).filter(
          (value) => value !== item.rawValue,
        );
    const nextCategoryData = {
      ...categoryData,
      [item.field]: nextValue,
      start: 0,
      refresh_key: Date.now(),
    };
    const nextFilterData = {
      ...filterData,
      [category]: nextCategoryData,
    };

    dispatch(setFilterData(nextFilterData));
  };

  const showing = categoryData.data?.length || 0;
  const total =
    productList?.category_totals?.[category] ??
    productList?.totalFiltered ??
    showing;
  // The count runs cumulatively through the result set, so page two of nine
  // reads 18 rather than 9 again.
  const showingLabel = (categoryData.start || 0) + showing;

  useEffect(() => {
    if (!sticky) return undefined;

    const updateReachedState = () => {
      const filteredValues = document.querySelector(
        ".home-product .filtered-values.sticky",
      );
      const header = document.querySelector(".header");

      if (!filteredValues || !header) return;

      const headerBounds = header.getBoundingClientRect();
      const hasReached =
        filteredValues.getBoundingClientRect().top <= headerBounds.bottom + 1;

      setHasReachedShowing((current) =>
        current === hasReached ? current : hasReached,
      );
      setStickyTop((current) =>
        Math.round(current) === Math.round(headerBounds.height)
          ? current
          : headerBounds.height,
      );
    };

    updateReachedState();
    // The page scrolls inside #site-frame on desktop but inside
    // .mobile-container below that, so listening on one of them leaves the
    // other silent. Scroll does not bubble, but it can be caught on the
    // document in the capture phase whichever container is doing the
    // scrolling.
    document.addEventListener("scroll", updateReachedState, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", updateReachedState);

    return () => {
      document.removeEventListener("scroll", updateReachedState, {
        capture: true,
      });
      window.removeEventListener("resize", updateReachedState);
    };
  }, [sticky]);

  if (summary.length === 0 && !showCount && !code) return null;

  return (
    <Row
      className={`_row filter-summary filtered-values ${
        sticky ? "sticky" : ""
      } ${hasReachedShowing ? "reach-showing" : ""} ${
        summary.length === 0 ? "no-filter" : ""
      } ${code ? "has-referral" : ""}`}
      style={sticky ? { "--filter-sticky-top": `${stickyTop}px` } : undefined}
    >
      {showCount && (
        <Col className={`_col listing-referral-block ${code ? "w-100" : ""}`}>
          {code && (
            <Row className="_row referral-bar">
              <Row className="_row referral-message">
                <Icon icon="solar:gift-linear" className="_svg referral" />
                <p className="_label referral-copy">
                  {t("Order with")}
                  <span className="_label referral-name">
                    {name || t("Friend")}
                    {t("'s Referral")}
                  </span>
                </p>
              </Row>
              <span className="referral-divider" />
              {isDesktop && (
                <p className="_label referral-code-copy">
                  {t("Referral Code:")}
                  <span className="_label referral-code">{code}</span>
                </p>
              )}
              <Button className="_button referral-share" onClick={cancel}>
                <span className="_label referral-share">{t("Cancel")}</span>
              </Button>
            </Row>
          )}
          {isDesktop && (
            <Row className="_row filtered-result-meta">
              <span className="_label filtered-result-count">
                {t("Showing")} {showingLabel} {t("of")} {total}
              </span>
            </Row>
          )}
        </Col>
      )}
      <Row className="_row filtered-value-list gap-2">
        {summary.map((item, index) => (
          <Button
            key={`${item.label}-${item.value}-${index}`}
            className="_button filter-summary-chip"
            onClick={() => removeFilter(item)}
          >
            <span className="_label filter-summary-label">{t(item.label)}</span>
            <span className="_label filter-summary-value">{item.value}</span>
            <Icon
              icon="material-symbols:close-rounded"
              className="_svg filter-summary-remove"
            />
          </Button>
        ))}
      </Row>
    </Row>
  );
}
